import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { watchEntireCollection } from '@adornis/baseql/server/watchQuery.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { map, of, switchMap } from 'rxjs';
import { bhBaseQlContext } from '../bh-context.js';
import { Participation } from '../entities/Participation.js';
import { BHFilter } from '../entities/bh-filter.js';
import { User } from '../entities/user.js';
import { Permission } from '../enums/roles.js';
import { UserDataview } from '../use-case-entities/UserDataview.js';

export const subscribeUserDataview = registerSubscription({
  operationName: 'subscribeUserDataview',
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => BHFilter },
  ],
  type: () => [UserDataview],
  resolve: (skip: number, limit: number, filter: BHFilter) => (gqlFields: BaseQLSelectionSet<UserDataview>) => {
    if (!filter.institutionID) return of([]);
    const institutionID = filter.institutionID;

    return watchEntireCollection('participations-materialized').pipe(
      switchMap(async () => {
        const currentUser = await CurrentUserInfo.getMyself<User>()({
          roles: AdornisRoleToContextsWrapper.allFields,
        });
        if (!currentUser?.hasPermission(Permission.VIEW_ADMIN_INSTITUTION_USERS))
          throw new Error('no permission to do that');

        bhBaseQlContext.institutionID = institutionID;

        if (gqlFields.user?.suspended) {
          gqlFields.user.suspentions = gqlFields.user.suspentions ?? {};
          gqlFields.user.suspentions.institutionID = 1;
        }

        const filter2 = {
          'product.institutionID': institutionID,
          ...(filter.timeframeID ? { 'product.timeframeID': filter.timeframeID } : {}),
        };

        const institutionSuspension = {
          $gt: [
            {
              $size: {
                $filter: {
                  input: { $ifNull: ['$user.suspentions', []] },
                  as: 'suspension',
                  cond: {
                    $eq: ['$$suspension.institutionID', institutionID],
                  },
                },
              },
            },
            0,
          ],
        };

        // Main aggregation pipeline
        const aggregation = [
          // { $lookup: lookupEntityById(Product, 'productID', 'product') },
          // { $set: { product: { $first: '$product' } } },
          // { $lookup: lookupEntityById(User, 'userID', 'user') },
          // { $set: { user: { $first: '$user' } } },
          { $match: filter2 },
          {
            $group: {
              _id: '$userID',
              participations: { $push: '$$ROOT' },
              user: { $first: '$user' },
              userStammdaten: { $first: '$userStammdaten' },
            },
          },
          { $unset: 'participation.user' },
          {
            $set: {
              institutionID: { $first: '$participations.product.institutionID' },
              timeframeID: { $first: '$participations.product.timeframeID' },
              'user.suspended': { $cond: { if: institutionSuspension, then: true, else: false } },
            },
          },
          { $match: filter.getMongoFilter() },
          { $addFields: { _class: UserDataview._class } },
          { $project: gqlFields },
          { $sort: filter.getMongoOptions().sort },
          { $skip: skip },
          { $limit: limit },
        ];

        const collection = await getRawCollection<Participation>('participations-materialized');
        const result = await collection.aggregate<UserDataview>(aggregation, { collation: { locale: 'de' } }).toArray();

        return result;
      }),
    );
  },
});

export const subscribeUserDataviewCount = registerSubscription({
  operationName: 'subscribeUserDataviewCount',
  type: () => Int,
  params: [{ name: 'filter', type: () => BHFilter }],

  resolve: (filter: BHFilter) => () => {
    return subscribeUserDataview(0, 99999999, filter)({ _id: 1 }).pipe(map(items => items.length));
  },
});
